import type { PassportCategory, PassportSchemaDef } from "../types.js";

/**
 * Cross-sector "compliance" category appended to every sector template
 * (draft TR ESPR Art. 9(5) substances of concern; EK-3 d conformity documents;
 * EK-3 k voluntary labels). It lives here, not in each schema file, because
 * the obligations are horizontal: every product group must be able to name
 * its substances of concern with CAS/EC numbers, location and concentration,
 * cite its declaration of conformity, and list voluntary labels.
 *
 * Field ids are STABLE persistence keys (like every schema field); sector
 * schemas that already carried a SCIP/REACH field mark it `deprecatedBy` the
 * common one rather than renaming.
 */
export const COMPLIANCE_CATEGORY_ID = "compliance";

export const COMPLIANCE_CATEGORY: PassportCategory = {
  id: COMPLIANCE_CATEGORY_ID,
  labels: { tr: "Uygunluk ve Maddeler", en: "Compliance & Substances", de: "Konformität & Stoffe" },
  fields: [
    {
      id: "compliance.substancesOfConcern",
      type: "group",
      required: false,
      tier: "public",
      labels: {
        tr: "Yüksek önem arz eden maddeler",
        en: "Substances of concern",
        de: "Besorgniserregende Stoffe",
      },
      description:
        "Substances of concern present above the reporting threshold (0.1% w/w for SVHC): name, CAS/EC number, where in the product, concentration, safe use.",
      itemFields: [
        {
          key: "name",
          type: "string",
          required: true,
          labels: { tr: "Madde adı (IUPAC/genel)", en: "Substance name (IUPAC/common)", de: "Stoffname" },
        },
        {
          key: "casNumber",
          type: "string",
          required: false,
          labels: { tr: "CAS no", en: "CAS number", de: "CAS-Nr." },
        },
        {
          key: "ecNumber",
          type: "string",
          required: false,
          labels: { tr: "EC no", en: "EC number", de: "EG-Nr." },
        },
        {
          key: "location",
          type: "string",
          required: true,
          labels: { tr: "Üründeki yeri", en: "Location in product", de: "Ort im Produkt" },
        },
        {
          key: "concentrationPct",
          type: "percentage",
          required: false,
          unit: "% w/w",
          labels: { tr: "Konsantrasyon", en: "Concentration", de: "Konzentration" },
        },
        {
          key: "concentrationRange",
          type: "string",
          required: false,
          labels: { tr: "Konsantrasyon aralığı", en: "Concentration range", de: "Konzentrationsbereich" },
        },
        {
          key: "safeUseInstructions",
          type: "string",
          required: false,
          labels: { tr: "Güvenli kullanım", en: "Safe use", de: "Sichere Verwendung" },
        },
        {
          key: "endOfLifeInstructions",
          type: "string",
          required: false,
          labels: { tr: "Ömür sonu işlemi", en: "End-of-life handling", de: "Entsorgungshinweis" },
        },
      ],
    },
    {
      id: "compliance.scipNumber",
      type: "string",
      required: false,
      tier: "restricted",
      labels: { tr: "SCIP kayıt numarası", en: "SCIP number", de: "SCIP-Nummer" },
      description: "ECHA SCIP database reference for articles containing SVHC above 0.1% w/w.",
    },
    {
      id: "compliance.reachDeclarationUrl",
      type: "url",
      required: false,
      tier: "restricted",
      labels: { tr: "REACH beyanı", en: "REACH declaration", de: "REACH-Erklärung" },
    },
    {
      id: "compliance.declarationOfConformityNo",
      type: "string",
      required: false,
      tier: "public",
      labels: {
        tr: "AB uygunluk beyanı no",
        en: "EU declaration of conformity no",
        de: "EU-Konformitätserklärung Nr.",
      },
    },
    {
      id: "compliance.declarationOfConformityUrl",
      type: "url",
      required: false,
      tier: "public",
      labels: {
        tr: "Uygunluk beyanı bağlantısı",
        en: "Declaration of conformity link",
        de: "Link Konformitätserklärung",
      },
    },
    {
      id: "compliance.ceMarking",
      type: "boolean",
      required: false,
      tier: "public",
      labels: { tr: "CE işareti", en: "CE marking", de: "CE-Kennzeichnung" },
    },
    {
      id: "compliance.notifiedBodyId",
      type: "string",
      required: false,
      tier: "restricted",
      labels: {
        tr: "Onaylanmış kuruluş no",
        en: "Notified body number",
        de: "Nummer der notifizierten Stelle",
      },
    },
    {
      id: "compliance.technicalFileRef",
      type: "string",
      required: false,
      tier: "restricted",
      labels: {
        tr: "Teknik dosya referansı",
        en: "Technical file reference",
        de: "Referenz technische Unterlagen",
      },
      description: "Where the confidential technical file is held (reference only — never the file itself).",
    },
    {
      id: "compliance.voluntaryLabels",
      type: "group",
      required: false,
      tier: "public",
      labels: { tr: "Gönüllü etiketler", en: "Voluntary labels", de: "Freiwillige Kennzeichnungen" },
      description: "EU Ecolabel and other voluntary labels the product carries (EK-3 k).",
      itemFields: [
        {
          key: "label",
          type: "string",
          required: true,
          labels: { tr: "Etiket", en: "Label", de: "Kennzeichnung" },
        },
        {
          key: "certificateNo",
          type: "string",
          required: false,
          labels: { tr: "Sertifika no", en: "Certificate no", de: "Zertifikat-Nr." },
        },
        { key: "url", type: "url", required: false, labels: { tr: "Bağlantı", en: "Link", de: "Link" } },
      ],
    },
  ],
};

/** Appends the common category unless the schema already carries one with the same id (idempotent). */
export function withCommonCategories(def: PassportSchemaDef): PassportSchemaDef {
  if (def.categories.some((c) => c.id === COMPLIANCE_CATEGORY_ID)) return def;
  return { ...def, categories: [...def.categories, COMPLIANCE_CATEGORY] };
}
